import { ArrowRight16Icon } from '@jablotron/lib.spa-icons/icons/arrow-right-16';
import { ChevronDown16Icon } from '@jablotron/lib.spa-icons/icons/chevron-down-16';
import { useTranslate } from '@jablotron/lib.spa-lang';
import { Link } from '@tanstack/react-router';
import { type ReactNode, useState } from 'react';
import type { SidebarCategory, SidebarSubcategory } from './CatalogSidebar';
import { Overline } from '~/components/overline/Overline';
import { getCatalogSync } from '~/entities/catalog/api';
import { CANONICAL_CATEGORIES, canonicalCategoryTitle } from '~/entities/catalog/canonical-categories';
import { SIDEBAR_SECTIONS } from '~/entities/catalog/sidebar-sections';
import { Texts } from '~/texts/runtime';

/** Every tappable row is at least 44px tall, so it's hittable with a thumb (or a glove on site). */
const ROW = 'flex min-h-11 w-full items-center gap-2 rounded-lg px-3 text-left no-underline';

function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <Overline as="h3" className="px-3 pb-1 pt-4">
      {children}
    </Overline>
  );
}

/** A row that only expands/collapses its children — tapping it never navigates away. */
function Disclosure({
  id,
  label,
  open,
  active = false,
  onToggle,
  children,
}: {
  id: string;
  label: string;
  open: boolean;
  active?: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <li>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={id}
        onClick={onToggle}
        className={`${ROW} cursor-pointer border-none bg-transparent text-base font-semibold ${
          active ? 'text-brand-cyanText' : 'text-brand-ink'
        }`}
      >
        {label}
        <ChevronDown16Icon
          width={16}
          height={16}
          aria-hidden
          className={`ml-auto shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <ul id={id} className="m-0 mb-1 flex list-none flex-col p-0 pl-3">
          {children}
        </ul>
      )}
    </li>
  );
}

function NavLink({ children, active = false, ...link }: { children: ReactNode; active?: boolean } & LinkTarget) {
  return (
    <li>
      <Link
        {...link}
        className={`${ROW} text-sm ${active ? 'font-semibold text-brand-cyanText' : 'font-normal text-brand-ink'}`}
      >
        {children}
      </Link>
    </li>
  );
}

type LinkTarget =
  | { to: '/categories/$categoryId'; params: { categoryId: string } }
  | { to: '/categories/$categoryId/$subcategoryId'; params: { categoryId: string; subcategoryId: string } }
  | { to: '/products'; search?: { category: string } }
  | { to: '/compatibility' }
  | { to: '/blog' };

function subcategoryLinks(cat: SidebarCategory, activeSubcategoryId: string | undefined, t: (key: string) => string) {
  const link = (sub: SidebarSubcategory) => (
    <NavLink
      key={sub.id}
      to="/categories/$categoryId/$subcategoryId"
      params={{ categoryId: cat.slug, subcategoryId: sub.slug }}
      active={sub.id === activeSubcategoryId || sub.slug === activeSubcategoryId}
    >
      {t(sub.name)}
    </NavLink>
  );

  if (!cat.subcategories.some((sub) => sub.section)) {
    return cat.subcategories.map(link);
  }

  return SIDEBAR_SECTIONS.flatMap((section) => {
    const items = cat.subcategories.filter((sub) => sub.section === section.key);

    return items.length === 0
      ? []
      : [
          <li key={section.key} className="px-3 pb-0.5 pt-2 text-caption uppercase tracking-wide text-brand-subtle">
            {t(Texts[section.textsKey])}
          </li>,
          ...items.map(link),
        ];
  });
}

/**
 * Hamburger-menu content for phones/tablets: product lines (tap to expand, then "View the product line"),
 * the cross-line product categories, all products, and the site-wide Compatibility and Blog pages.
 */
export function MobileCatalogNav({
  categories,
  activeCategoryId,
  activeSubcategoryId,
  onNavigate,
}: {
  categories: SidebarCategory[];
  activeCategoryId?: string;
  activeSubcategoryId?: string;
  /** Called when any link is tapped — including one to the current page, which wouldn't change the URL. */
  onNavigate?: () => void;
}) {
  const t = useTranslate();
  const activeCategory = categories.find((cat) => cat.id === activeCategoryId || cat.slug === activeCategoryId);
  const [openId, setOpenId] = useState<string | null>(activeCategory?.id ?? null);
  const toggle = (id: string) => setOpenId((prev) => (prev === id ? null : id));
  const subcategoryPages = getCatalogSync().subcategoryPages;

  return (
    <nav
      className="-mx-3 pb-6"
      onClick={(event) => {
        if ((event.target as HTMLElement).closest('a')) {
          onNavigate?.();
        }
      }}
    >
      <SectionHeading>{t(Texts.MegaMenuLinesHeading)}</SectionHeading>
      <ul className="m-0 flex list-none flex-col p-0">
        {categories.map((cat) => (
          <Disclosure
            key={cat.id}
            id={`mobile-nav-line-${cat.slug}`}
            label={t(cat.title)}
            open={openId === cat.id}
            active={cat.id === activeCategory?.id}
            onToggle={() => toggle(cat.id)}
          >
            <NavLink to="/categories/$categoryId" params={{ categoryId: cat.slug }}>
              <span className="inline-flex items-center gap-1 font-semibold text-brand-cyanText">
                {t(Texts.MobileMenuViewProductLine)}
                <ArrowRight16Icon width={14} height={14} aria-hidden />
              </span>
            </NavLink>
            {/* Subcategory slugs repeat across lines (Flexi and Mercury both have `detectors`). */}
            {subcategoryLinks(cat, cat.id === activeCategory?.id ? activeSubcategoryId : undefined, t)}
          </Disclosure>
        ))}
      </ul>

      <ul className="m-0 mt-2 flex list-none flex-col border-t border-primary p-0 pt-2">
        <Disclosure
          id="mobile-nav-categories"
          label={t(Texts.MegaMenuCategoriesHeading)}
          open={openId === 'categories'}
          onToggle={() => toggle('categories')}
        >
          {CANONICAL_CATEGORIES.map((category) => (
            <NavLink key={category.slug} to="/products" search={{ category: category.slug }}>
              {t(canonicalCategoryTitle(category, subcategoryPages))}
            </NavLink>
          ))}
        </Disclosure>
        <NavLink to="/products">
          <span className="text-base font-semibold">{t(Texts.CatalogAllProducts)}</span>
        </NavLink>
      </ul>

      <ul className="m-0 mt-2 flex list-none flex-col border-t border-primary p-0 pt-2">
        <NavLink to="/compatibility">
          <span className="text-base font-semibold">{t(Texts.FooterProductCompatibility)}</span>
        </NavLink>
        <NavLink to="/blog">
          <span className="text-base font-semibold">{t(Texts.NavBlog)}</span>
        </NavLink>
      </ul>
    </nav>
  );
}
